import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import axios from "axios";

const WA_NUMBER = "918097237982";
const DEFAULT_MSG =
  "Hi FOXEL, I saw your portfolio and would like to discuss a project.";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const logClick = (payload) => {
  // fire-and-forget; never block the WhatsApp handoff
  try {
    axios.post(`${API}/wa-click`, payload).catch(() => {});
  } catch (_) {
    /* ignore */
  }
};

// Official WhatsApp glyph (simple-icons)
const WhatsAppIcon = ({ size = 26 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.1-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
  </svg>
);

export const WhatsAppButton = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const url = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(DEFAULT_MSG)}`;

  return (
    <div className="fixed z-[80] right-5 md:right-8 bottom-5 md:bottom-8 flex items-end gap-3">
      <AnimatePresence>
        {show && !dismissed && (
          <motion.div
            key="wa-tip"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.35 }}
            className="hidden sm:flex items-center gap-2 bg-white rounded-full shadow-2xl border border-[#E5DEE5] pl-4 pr-2 py-2"
          >
            <span className="text-[#1A1518] text-sm font-medium">
              Chat with the studio
            </span>
            <button
              onClick={() => setDismissed(true)}
              className="w-7 h-7 rounded-full bg-[#F4F1F5] hover:bg-[#E5DEE5] flex items-center justify-center text-[#4D454A] transition-colors"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.a
        data-testid="whatsapp-fab"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => logClick({ source: "fab", referrer: window.location.pathname })}
        initial={{ scale: 0, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.6, type: "spring", stiffness: 160, damping: 14 }}
        className="relative group w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] hover:bg-[#20b358] shadow-xl shadow-emerald-500/30 flex items-center justify-center text-white transition-colors duration-200"
        aria-label="Chat on WhatsApp"
      >
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-70 animate-ping pointer-events-none" style={{ animationDuration: "2.4s" }} />
        <WhatsAppIcon size={30} />
      </motion.a>
    </div>
  );
};
